import { createContext, useContext, useEffect, useCallback, useRef, useState } from 'react';

import { useAuth } from './AuthContext';
import { supabase } from '@/lib/supabase';
import { useToast } from '@/hooks/use-toast';
import { captureError } from '@/lib/sentry';
import type { Workspace } from '@/types/workspace';

interface WorkspaceContextType {
  workspaces: Workspace[];
  currentWorkspace: Workspace | null;
  loading: boolean;
  switchWorkspace: (workspaceId: string) => Promise<void>;
  refetch: () => Promise<void>;
}

const WorkspaceContext = createContext<WorkspaceContextType | undefined>(undefined);

const MAX_FETCH_RETRIES = 3;

export function WorkspaceProvider({ children }: { children: React.ReactNode }) {
  const { user, loading: authLoading } = useAuth();
  const { toast } = useToast();
  
  
  const [workspaces, setWorkspaces] = useState<Workspace[]>([]);
  const [currentWorkspace, setCurrentWorkspace] = useState<Workspace | null>(null);
  const [loading, setLoading] = useState(true);
  
  // Reference to track if initial fetch has completed
  const initialFetchCompleteRef = useRef(false);
  const fetchInProgressRef = useRef(false);
  const authReadyRef = useRef(false);
  const retryCountRef = useRef(0);
  const retryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Latest signed-in user id, readable from stale closures. The retry timer
  // below is armed inside fetchWorkspaces, which closes over `user`; without
  // this a retry armed before a session swap fires afterwards and requests the
  // PREVIOUS user's workspaces under the NEW user's JWT. A ref (not a dep) is
  // deliberate: `user` is a fresh object on every route change, so keying the
  // cleanup effect on it would clear the timer constantly and unbind the retry
  // limit that commit 01a3b1d added.
  const userIdRef = useRef<string | undefined>(user?.id);
  useEffect(() => {
    userIdRef.current = user?.id;
  }, [user]);

  const resetWorkspaceStates = useCallback(() => {
    setWorkspaces([]);
    setCurrentWorkspace(null);
  }, []);

  const fetchWorkspaces = useCallback(async (force = false) => {
    if (!user) {
      resetWorkspaceStates();
      setLoading(false);
      return;
    }

    // Prevent multiple concurrent fetches
    if (fetchInProgressRef.current && !force) return;
    fetchInProgressRef.current = true;

    try {
      setLoading(true);
      console.log('[WorkspaceContext] Fetching workspaces for user:', user.id);
  
      // Fetch workspaces using the RPC function
      const { data: workspaceData, error: workspaceError } = await supabase.rpc(
        'get_workspace_details',
        { p_user_id: user.id }
      );
  
      if (workspaceError) {
        console.error('[WorkspaceContext] Error fetching workspaces:', workspaceError);
        throw workspaceError;
      }
      
      // Create a personal workspace if none exists
      if (!workspaceData?.length) {
        console.log('[WorkspaceContext] No workspaces found, creating personal workspace');
        
        const { data: workspace, error: createError } = await supabase
          .from('workspaces')
          .insert({
            name: `${user.email}'s Workspace`,
            created_by: user.id,
            is_personal: true,
          })
          .select()
          .single();
  
        if (createError) throw createError;
  
        const { error: memberError } = await supabase
          .from('workspace_members')
          .insert({
            workspace_id: workspace.id,
            user_id: user.id,
            role: 'admin',
            status: 'active',
          });
  
        if (memberError) throw memberError;
  
        const newWorkspace = {
          ...workspace,
          role: 'admin',
          member_count: 1,
          contact_count: 0,
        };
  
        setWorkspaces([newWorkspace]);
        setCurrentWorkspace(newWorkspace);

        localStorage.setItem('currentWorkspaceId', workspace.id);
        initialFetchCompleteRef.current = true;
        retryCountRef.current = 0;
        setLoading(false);
        fetchInProgressRef.current = false;
        return;
      }

      console.log(`[WorkspaceContext] Fetched ${workspaceData.length} workspaces`);
      setWorkspaces(workspaceData);

      // Set active workspace if not already set
      const savedWorkspaceId = localStorage.getItem('currentWorkspaceId');
            const savedWorkspace = workspaceData.find((w: Workspace) => w.id === savedWorkspaceId);
      
      let newCurrentWorkspace = savedWorkspace;
      
      if (!newCurrentWorkspace) {
        // Default to personal workspace or first workspace
                const personalWorkspace = workspaceData.find((w: Workspace) => w.is_personal);
        newCurrentWorkspace = personalWorkspace || workspaceData[0];
        
        if (newCurrentWorkspace) {
          console.log(`[WorkspaceContext] Setting default workspace: ${newCurrentWorkspace.name}`);
          localStorage.setItem('currentWorkspaceId', newCurrentWorkspace.id);
        }
      }
      
      setCurrentWorkspace(newCurrentWorkspace);
      initialFetchCompleteRef.current = true;
      retryCountRef.current = 0;
    } catch (error) {
      console.error('[WorkspaceContext] Error fetching workspaces:', error);

      if (retryCountRef.current < MAX_FETCH_RETRIES) {
        retryCountRef.current += 1;
        const delay = 2000 * Math.pow(2, retryCountRef.current - 1); // 2s, 4s, 8s
        if (retryTimerRef.current) clearTimeout(retryTimerRef.current);
        const scheduledForUserId = user.id;
        retryTimerRef.current = setTimeout(() => {
          // Drop the retry if the session swapped while it was pending. This
          // closure holds the PREVIOUS user; re-running it would ask
          // get_workspace_details for their workspaces under the new user's
          // JWT, which the function now rejects.
          if (userIdRef.current !== scheduledForUserId) return;
          fetchInProgressRef.current = false;
          fetchWorkspaces(true);
        }, delay);
      } else {
        // Max retries reached — stop retrying so the main effect doesn't keep re-firing
        captureError(error as Error, { feature: 'workspace-fetch', retriesExhausted: true });
        toast({
          title: "Error",
          description: "Failed to load workspaces. Please refresh the page.",
          variant: "destructive",
        });
        initialFetchCompleteRef.current = true;
      }

      resetWorkspaceStates();

      // Clear localStorage in case it's corrupted
      if (user) {
        localStorage.removeItem('currentWorkspaceId');
      }
    } finally {
      setLoading(false);
      fetchInProgressRef.current = false;
    }
  }, [user, toast, resetWorkspaceStates]);

  const switchWorkspace = useCallback(async (workspaceId: string) => {
    const workspace = workspaces.find((w) => w.id === workspaceId);
    if (!workspace || workspace.id === currentWorkspace?.id) return;

    try {
      setLoading(true);
      localStorage.setItem('currentWorkspaceId', workspaceId);
      setCurrentWorkspace(workspace);

      toast({
        description: `Switched to ${workspace.name}`,
      });

      // Reload the page to refresh all data for the new workspace
      window.location.reload();
    } catch (error) {
      console.error('Error switching workspace:', error);
      captureError(error as Error, { feature: 'workspace-switch' });
      toast({
        title: "Error",
        description: "Failed to switch workspace",
        variant: "destructive",
      });
      await fetchWorkspaces(true);
    } finally {
      setLoading(false);
    }
  }, [workspaces, currentWorkspace, toast, fetchWorkspaces]);

  // Track when auth is ready to prevent premature fetch attempts
  useEffect(() => {
    if (!authLoading) {
      authReadyRef.current = true;
    }
  }, [authLoading]);

  // Cleanup pending retry timers on unmount
  useEffect(() => {
    return () => {
      if (retryTimerRef.current) clearTimeout(retryTimerRef.current);
    };
  }, []);

  // Main effect to fetch workspaces when auth is ready
  useEffect(() => {
    // Only attempt to fetch when:
    // 1. Auth is ready (not loading)
    // 2. We have a user
    // 3. Initial fetch hasn't been done
    // 4. No fetch is currently in progress
    if (
      !authLoading && 
      user && 
      authReadyRef.current && 
      !initialFetchCompleteRef.current && 
      !fetchInProgressRef.current
    ) {
      console.log('[WorkspaceContext] Auth ready, starting initial workspace fetch');
      fetchWorkspaces(true);
    } 
    // Reset when user is null (logged out)
    else if (!user && authReadyRef.current) {
      console.log('[WorkspaceContext] User logged out, resetting workspace state');
      resetWorkspaceStates();
      initialFetchCompleteRef.current = false;
    }
  }, [user, authLoading, resetWorkspaceStates, fetchWorkspaces]);

  return (
    <WorkspaceContext.Provider value={{
      workspaces,
      currentWorkspace,
      loading,
      switchWorkspace,
      refetch: () => fetchWorkspaces(true),
    }}>
      {children}
    </WorkspaceContext.Provider>
  );
}

export function useWorkspaces() {
  const context = useContext(WorkspaceContext);
  if (context === undefined) {
    throw new Error('useWorkspaces must be used within a WorkspaceProvider');
  }
  return context;
}